import { useState, useEffect, useCallback, useRef } from 'react'
import { Table } from '@tanstack/react-table'
import { DataTableV2DataRow } from '../types'
import { FormData } from '../../DataFormComponent/types'
import { JsonFormPropertyV2 } from '../../FormComponent/types'
import { transformValuesToTitleValueObjects } from '../utils'

interface UseTableRegroupingV2Props {
  groupedData: DataTableV2DataRow[]
  defaultExpanded: boolean
  tableRef: React.MutableRefObject<Table<DataTableV2DataRow> | null>
}

export function useTableRegroupingV2({ groupedData, defaultExpanded, tableRef }: UseTableRegroupingV2Props) {
  // Local state for drag-and-drop regrouping
  const [localData, setLocalData] = useState<DataTableV2DataRow[]>(groupedData)

  // Track expanded state for controlled expansion
  const [expandedState, setExpandedState] = useState<Record<string, boolean>>({})
  const expandedStateRef = useRef<Record<string, boolean>>({})

  // Sync localData with groupedData when props change
  useEffect(() => {
    setLocalData(groupedData)
  }, [groupedData])

  const handleExpandedChange = useCallback(
    (updater: unknown) => {
      setExpandedState((prev) => {
        // Determine the current expanded state from TanStack Table's perspective
        // If prev is empty and defaultExpanded is true, the table sees `expanded: true`
        const currentTableState = Object.keys(prev).length > 0 ? prev : defaultExpanded ? true : {}

        // Apply the updater with the correct current state
        const newValue = typeof updater === 'function' ? updater(currentTableState) : updater

        // Handle both true (all expanded) and Record<string, boolean> cases
        let result: Record<string, boolean>
        if (newValue === true) {
          result = {}
        } else {
          result = newValue as Record<string, boolean>
        }
        expandedStateRef.current = result
        return result
      })
    },
    [defaultExpanded]
  )

  // Handler to ungroup a subrow (move it back to top-level)
  const handleUngroup = useCallback((rowId: string) => {
    setLocalData((prevData) => {
      const newData = [...prevData]
      let foundRow: DataTableV2DataRow | null = null
      let parentGroupIndex = -1

      // Find the row and its parent group
      for (let i = 0; i < newData.length; i++) {
        const item = newData[i] as DataTableV2DataRow & {
          isGroup?: boolean
          subRows?: DataTableV2DataRow[]
        }
        if (item.isGroup && item.subRows) {
          const subRowIndex = item.subRows.findIndex((subRow) => String(subRow.id) === String(rowId))
          if (subRowIndex !== -1) {
            foundRow = item.subRows[subRowIndex]
            parentGroupIndex = i
            break
          }
        }
      }

      if (!foundRow || parentGroupIndex === -1) return prevData

      // Remove the row from its parent group's subRows
      const parentGroup = { ...newData[parentGroupIndex] } as DataTableV2DataRow & {
        subRows: DataTableV2DataRow[]
      }
      parentGroup.subRows = parentGroup.subRows.filter((subRow) => String(subRow.id) !== String(rowId))

      // Keep the group even if it has no more subrows
      newData[parentGroupIndex] = parentGroup

      // Add the ungrouped row to top-level
      newData.push(foundRow)

      return newData
    })
  }, [])

  // Handler to update a group row's data locally
  const handleUpdateGroup = useCallback((groupId: string, data: FormData, formProperties: JsonFormPropertyV2[]) => {
    // console.log('groupId', groupId)
    const updatedRow = transformValuesToTitleValueObjects(data, formProperties)
    // console.log('original row', data)
    // console.log('transformed row', updatedRow)
    setLocalData((prevData) => {
      const newData = [...prevData]
      const index = newData.findIndex((item) => String(item.id) === String(groupId))
      if (index !== -1) {
        newData[index] = {
          ...newData[index],
          ...(updatedRow as Partial<DataTableV2DataRow>),
        }
      }
      return newData
    })
  }, [])

  // Handler to delete a group row locally and orphan its subrows
  const handleDeleteGroup = useCallback((groupId: string) => {
    setLocalData((prevData) => {
      const newData = [...prevData]
      const index = newData.findIndex((item) => String(item.id) === String(groupId))

      if (index !== -1) {
        const group = newData[index] as DataTableV2DataRow & {
          subRows?: DataTableV2DataRow[]
        }
        // Extract subrows to orphan them
        const subRows = group.subRows || []

        // Remove the group
        newData.splice(index, 1)

        // Add subrows to top-level
        if (subRows.length > 0) {
          newData.push(...subRows)
        }
      }
      return newData
    })
  }, [])

  // Handler for drop operations
  const handleDrop = useCallback(
    (draggedRowId: string, targetRowId: string) => {
      // Get the current expanded state from the table instance if available
      let wasExpanded = false
      if (tableRef.current) {
        const allRows = tableRef.current.getRowModel().rows
        const targetRow = allRows.find((row) => String(row.id) === String(targetRowId))
        if (targetRow) {
          wasExpanded = targetRow.getIsExpanded()
        }
      } else {
        // Fallback: check from expanded state ref
        const currentExpanded = expandedStateRef.current
        wasExpanded =
          currentExpanded[targetRowId] === true || (defaultExpanded && currentExpanded[targetRowId] !== false)
      }

      setLocalData((prevData) => {
        const newData = [...prevData]
        let draggedRow: DataTableV2DataRow | null = null
        let draggedRowIndex = -1
        let targetRow: DataTableV2DataRow | null = null
        let targetRowIndex = -1

        // Find both rows first (before any modifications)
        for (let i = 0; i < newData.length; i++) {
          const item = newData[i] as DataTableV2DataRow & { isGroup?: boolean }
          const itemId = String(item.id || '')

          // Find dragged row (must be an orphan, not a group)
          if (itemId === String(draggedRowId) && !item.isGroup) {
            draggedRow = item
            draggedRowIndex = i
          }

          // Find target row (can be orphan or group)
          if (itemId === String(targetRowId)) {
            targetRow = item
            targetRowIndex = i
          }

          // Early exit if both found
          if (draggedRow && targetRow) break
        }

        if (!draggedRow || !targetRow) return prevData

        const targetItem = targetRow as DataTableV2DataRow & {
          isGroup?: boolean
          subRows?: DataTableV2DataRow[]
        }

        // Case 1: Drop on another orphan (create new group)
        if (!targetItem.isGroup) {
          // Remove both rows from top-level (remove higher index first to avoid index shifting issues)
          if (draggedRowIndex > targetRowIndex) {
            newData.splice(draggedRowIndex, 1)
            newData.splice(targetRowIndex, 1)
          } else {
            newData.splice(targetRowIndex, 1)
            newData.splice(draggedRowIndex, 1)
          }

          // Create a new group based on the target row
          const newGroupId =
            typeof crypto !== 'undefined' && crypto.randomUUID
              ? crypto.randomUUID()
              : `group-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`

          const newGroup = {
            ...targetItem,
            id: newGroupId,
            subRows: [targetItem, draggedRow],
            isGroup: true,
            isExpanded: false,
          } as DataTableV2DataRow

          // Add new group to the end
          newData.push(newGroup)

          // New groups should be collapsed by default
          const currentExpanded = { ...expandedStateRef.current }
          delete currentExpanded[newGroupId]
          setExpandedState(currentExpanded)
          expandedStateRef.current = currentExpanded
        }
        // Case 2: Drop on an existing group (add to subRows)
        else if (targetItem.isGroup) {
          // Remove dragged row from top-level first
          newData.splice(draggedRowIndex, 1)

          // Find the target group again after removal (index may have shifted)
          let updatedTargetIndex = targetRowIndex
          if (draggedRowIndex < targetRowIndex) {
            updatedTargetIndex = targetRowIndex - 1
          }

          // Get fresh reference to target group
          const updatedTargetGroup = newData[updatedTargetIndex] as DataTableV2DataRow & {
            subRows?: DataTableV2DataRow[]
          }
          if (updatedTargetGroup && String(updatedTargetGroup.id) === String(targetRowId)) {
            const updatedGroup = {
              ...updatedTargetGroup,
              subRows: [...(updatedTargetGroup.subRows || []), draggedRow],
            }
            newData[updatedTargetIndex] = updatedGroup

            // Preserve the expanded state we captured earlier
            const currentExpanded = { ...expandedStateRef.current }
            if (wasExpanded) {
              currentExpanded[targetRowId] = true
            } else {
              currentExpanded[targetRowId] = false
            }
            setExpandedState(currentExpanded)
            expandedStateRef.current = currentExpanded
          }
        }

        return newData
      })
    },
    [setExpandedState, defaultExpanded, tableRef]
  )

  const handleResetGroups = useCallback(() => {
    setLocalData(groupedData)
  }, [groupedData])

  return {
    localData,
    setLocalData,
    expandedState,
    setExpandedState,
    handleExpandedChange,
    handleUngroup,
    handleDrop,
    handleUpdateGroup,
    handleDeleteGroup,
    handleResetGroups,
  }
}
